import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { accountBackupsAPI } from '@hpanel/web-common/api/client'
import type { BackupContents } from '@hpanel/web-common/api/client'
import { RestoreJobResult } from '../../components/Backups/RestoreJobResult'

/** Order home entries so domain directories come first. */
function orderHome(home: string[], domains: string[]): string[] {
  const d = new Set(domains)
  return [...home.filter((h) => d.has(h)), ...home.filter((h) => !d.has(h))]
}

/**
 * RestoreBackupPage — route: /client/backups/:id/restore (CLAUDE.md §15).
 * Selective restore: pick home entries / databases / mailboxes, optional
 * exclude globs, then poll the restore job.
 */
export const RestoreBackupPage: React.FC = () => {
  const { id } = useParams()
  const backupId = Number(id)
  const [contents, setContents] = useState<BackupContents | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [home, setHome] = useState<Set<string>>(new Set())
  const [dbs, setDbs] = useState<Set<string>>(new Set())
  const [mail, setMail] = useState<Set<string>>(new Set())
  const [exclude, setExclude] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [restoreId, setRestoreId] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    accountBackupsAPI
      .contents(backupId)
      .then((c) => {
        if (cancelled) return
        setContents(c)
        setHome(new Set(c.home))
        setDbs(new Set(c.databases))
        setMail(new Set(c.mailboxes))
      })
      .catch((e: unknown) => {
        if (cancelled) return
        console.error('RestoreBackupPage: failed to load contents', e)
        setLoadError(e instanceof Error ? e.message : 'Failed to load backup contents')
      })
    return () => {
      cancelled = true
    }
  }, [backupId])

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, key: string) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setter(next)
  }

  const nothingSelected = home.size + dbs.size + mail.size === 0

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contents) return
    const include: string[] = [
      ...contents.home.filter((h) => home.has(h)).map((h) => `home/${h}/**`),
      ...contents.databases.filter((d) => dbs.has(d)).map((d) => `databases/${d}.sql`),
      ...contents.mailboxes
        .filter((m) => mail.has(m))
        .map((m) => {
          const at = m.lastIndexOf('@')
          return `mail/${m.slice(at + 1)}/${m.slice(0, at)}/**`
        }),
    ]
    const excl = exclude.split('\n').map((l) => l.trim()).filter(Boolean)
    setSubmitting(true)
    setSubmitError(null)
    try {
      const job = await accountBackupsAPI.restore(backupId, { include, exclude: excl })
      setRestoreId(job.id)
    } catch (err: unknown) {
      console.error('RestoreBackupPage: restore request failed', err)
      setSubmitError(err instanceof Error ? err.message : 'Restore failed to start')
      setSubmitting(false)
    }
  }

  const group = (
    title: string,
    testid: string,
    items: string[],
    set: Set<string>,
    setter: (s: Set<string>) => void,
  ) => (
    <fieldset className="card space-y-2" data-testid={testid}>
      <legend className="font-semibold text-gray-900 dark:text-gray-100">{title}</legend>
      {items.length === 0 && <p className="text-sm text-gray-500">None in this backup.</p>}
      {items.map((it) => (
        <label key={it} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
          <input type="checkbox" checked={set.has(it)} onChange={() => toggle(set, setter, it)} />
          {it}
        </label>
      ))}
    </fieldset>
  )

  return (
    <div data-testid="restore-backup-page" className="space-y-6 max-w-3xl">
      <div>
        <Link to="/client/backups" className="text-sm text-primary-600 hover:underline">← Backups</Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">Restore backup #{id}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-testid="restore-settings-note">
          Files, databases and mailboxes can be restored here. Account settings and DNS are restored only by an administrator.
        </p>
      </div>

      {loadError && (
        <div data-testid="restore-load-error" role="alert" className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded-lg p-3 text-sm">
          {loadError}
        </div>
      )}
      {!contents && !loadError && <p className="text-sm text-gray-500">Loading backup contents…</p>}

      {contents && restoreId === null && (
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          {group('Files (home directory)', 'restore-group-home', orderHome(contents.home, contents.domains), home, setHome)}
          {group('Databases', 'restore-group-databases', contents.databases, dbs, setDbs)}
          {group('Mailboxes', 'restore-group-mail', contents.mailboxes, mail, setMail)}
          <div className="card space-y-1">
            <label htmlFor="restore-exclude" className="font-semibold text-gray-900 dark:text-gray-100">
              Exclude paths (optional, one glob per line)
            </label>
            <textarea
              id="restore-exclude"
              data-testid="restore-exclude"
              rows={3}
              value={exclude}
              onChange={(e) => setExclude(e.target.value)}
              placeholder="home/a.test/cache/**"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          {submitError && (
            <div data-testid="restore-submit-error" role="alert" className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded-lg p-3 text-sm">
              {submitError}
            </div>
          )}
          <button
            type="submit"
            data-testid="restore-submit"
            disabled={submitting || nothingSelected}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm rounded-lg font-medium"
          >
            {submitting ? 'Starting…' : 'Restore selected'}
          </button>
        </form>
      )}

      {restoreId !== null && <RestoreJobResult restoreId={restoreId} />}
    </div>
  )
}
